import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  getRaffleSlotsAction,
  redirectAction,
  updateRaffleWinnersAction,
} from "store/actions";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { OrbatNode } from "components/event/OrbatNode";
import slotIcons from "components/images";
import get from "lodash/get";
import HiddenOn from "components/common/HiddenOn";
import NotFound from "components/NotFound";
import { getAllRaffleEntrants } from "components/raffle/utils";
import InfoboxImportant from "components/raffle/InfoBoxImportant";
import InfoBoxInstructions from "components/raffle/InfoBoxInstructions";

export const Raffle = ({
  redirect,
  getRaffleSlots,
  raffles,
  error,
  updateWinners,
}) => {
  const [selection, setSelection] = useState({});
  let { id } = useParams<{ id }>();
  useEffect(() => {
    getRaffleSlots(id);
  }, [getRaffleSlots, id]);

  useEffect(() => {
    setSelection(getAllRaffleEntrants(raffles, id));
  }, [raffles, id]);

  const handleChange = ({ player_id, player_name, slot_id }) => {
    setSelection({
      ...selection,
      [id + slot_id]: {
        player_id,
        player_name,
      },
    });
  };

  const getClassName = ({ id, slot_id, player_id }) => {
    const role = selection[id + slot_id];
    if (!role) {
      return "unselected";
    }
    return role.player_id === player_id ? "selected" : "unselected";
  };

  return (
    <HiddenOn
      hideOn={error}
      fallback={
        <NotFound text={"There was a problem, please try again later."} />
      }
    >
      <div style={{ background: "#292b2f" }}>
        <InfoContainer>
          <InfoBoxInstructions
            updateWinners={updateWinners}
            selection={selection}
            id={id}
            hasRaffleSlots={raffles.length}
          />
          <InfoboxImportant />
        </InfoContainer>
        <Wrapper>
          <HiddenOn
            hideOn={!raffles.length}
            fallback={
              <NotFound text={"There are no raffle slots in this mission."} />
            }
          >
            {raffles.map(({ group_data, slots }, index) => {
              const item = {
                nodeData: {
                  group_data,
                  slots,
                },
              };
              return (
                <div key={index}>
                  {slots
                    .filter(({ slot_locked }) => !slot_locked)
                    .map((slot) => {
                      const { slot_shortcode, slot_id, raffle_entrants } = slot;
                      const target = slot_shortcode + slot_id;
                      return (
                        <InternalContainer target={target} key={target}>
                          <OrbatNode
                            className={"orbat"}
                            item={item}
                            side={""}
                          />
                          <Entrants>
                            <div
                              onClick={() =>
                                handleChange({
                                  player_id: "",
                                  player_name: "",
                                  slot_id,
                                })
                              }
                              className={getClassName({
                                id,
                                slot_id,
                                player_id: "",
                              })}
                            >
                              RANDOM
                            </div>
                            {raffle_entrants.map((entrant) => {
                              const { player_id, player_name } = entrant;

                              return (
                                <SelectablePlayer
                                  className={getClassName({
                                    id,
                                    slot_id,
                                    player_id,
                                  })}
                                  key={slot_id + player_id}
                                  onClick={() =>
                                    handleChange({
                                      player_id,
                                      player_name,
                                      slot_id,
                                    })
                                  }
                                >
                                  <img
                                    src={slotIcons["OFFICER"]}
                                    width="48"
                                    height="48"
                                    alt={""}
                                  />
                                  <div>Player: {player_name}</div>
                                  <div>ID: {player_id}</div>
                                </SelectablePlayer>
                              );
                            })}
                          </Entrants>
                        </InternalContainer>
                      );
                    })}
                </div>
              );
            })}
          </HiddenOn>
        </Wrapper>
      </div>
    </HiddenOn>
  );
};

const SelectablePlayer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Entrants = styled.div`
  display: flex;
  overflow: auto;
  > div {
    min-width: 200px;
  }

  //the random box
  div:first-child {
    display: flex;
    align-items: center;
    place-content: center;
  }

  .selected {
    margin: 15px;
    height: 150px;
    -moz-box-shadow: inset 0 0 10px #00ff00;
    -webkit-box-shadow: inset 0 0 10px #00ff00;
    box-shadow: inset 0 0 10px #00ff00;
  }

  .unselected {
    margin: 15px;
    height: 150px;
    -moz-box-shadow: 1px 1px 1px 1px #ccc;
    -webkit-box-shadow: 1px 1px 1px 1px #ccc;
    box-shadow: 1px 1px 1px 1px #ccc;
  }
`;

const InternalContainer = styled.div`
  .orbat {
    min-width: 250px;
  }
  display: flex;
  margin: 10px;
  border-bottom: 1px dashed #3b618ccc;
  padding: 10px;

  #${(props) => props.target} {
    > div {
      border: 1px dashed red;
      width: 96%;
    }
  }
`;

const Wrapper = styled.div`
  text-align: center;
  border-style: solid;
  border-color: #3b618ccc;
  border-radius: 8px;
  background-color: #292b2f;
  border-width: 2px;
  padding: 5px;
  margin: 10px;

  #tooltipChildrenWrapper {
    place-content: center;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  > div {
    width: 50%;
    text-align: left;
    .important {
      color: red;
    }
  }
`;

const mapDispatchToProps = {
  redirect: redirectAction,
  getRaffleSlots: getRaffleSlotsAction,
  updateWinners: updateRaffleWinnersAction,
};

const mapStateToProps = (state) => ({
  raffles: get(state, "rootReducer.raffle.raffleResponse", []),
  error: get(state, "rootReducer.raffle.error"),
  state: state,
});

export default connect(mapStateToProps, mapDispatchToProps)(Raffle);
